'use client';

import { useEffect, useState } from 'react';
import { Search, Loader2, CreditCard, ChevronLeft, ChevronRight, User, Shield, Trash2 } from 'lucide-react';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tier: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  twoFactorEnabled: boolean;
  totalBalance: number;
  withdrawableBalance: number;
  totalWithdrawn: number;
  activeNumbers: number;
  totalReferrals: number;
  createdAt: string;
  lastLoginAt: string | null;
  status: 'active' | 'suspended' | 'banned';
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'suspend' | 'unsuspend' | 'ban' | 'unban' | 'reset_password'>('suspend');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(roleFilter && { role: roleFilter }),
        ...(tierFilter && { tier: tierFilter }),
      });
      const response = await adminApi.getUsers(params.toString()) as { items: AdminUser[]; totalPages: number } | null;
      if (response && response.items) {
        setUsers(response.items);
        setTotalPages(response.totalPages);
      }
    } catch (err) {
      toast.error('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, search, statusFilter, roleFilter, tierFilter]);

  const handleAction = async (user: AdminUser, type: 'suspend' | 'unsuspend' | 'ban' | 'unban' | 'reset_password') => {
    setSelectedUser(user);
    setActionType(type);
    setActionDialogOpen(true);
  };

  const confirmAction = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      let endpoint = '';
      switch (actionType) {
        case 'suspend': endpoint = `/admin/users/${selectedUser.id}/suspend`; break;
        case 'unsuspend': endpoint = `/admin/users/${selectedUser.id}/unsuspend`; break;
        case 'ban': endpoint = `/admin/users/${selectedUser.id}/ban`; break;
        case 'unban': endpoint = `/admin/users/${selectedUser.id}/unban`; break;
        case 'reset_password': endpoint = `/admin/users/${selectedUser.id}/reset-password`; break;
      }
      const response = await adminApi.request('POST', endpoint);
      if (response) {
        toast.success(`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} successful`);
        fetchUsers();
      } else {
        toast.error('Action failed');
      }
    } catch (err) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
      setActionDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const getStatusBadge = (status: string): React.ReactElement => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500/10 text-green-400">Active</Badge>;
      case 'suspended': return <Badge className="bg-amber-500/10 text-amber-400">Suspended</Badge>;
      case 'banned': return <Badge className="bg-red-500/10 text-red-400">Banned</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRoleBadge = (role: string): React.ReactElement => {
    const colors: Record<string, string> = {
      super_admin: 'bg-purple-500/10 text-purple-400',
      admin: 'bg-blue-500/10 text-blue-400',
      user: 'bg-slate-500/10 text-slate-400',
    };
    return <Badge className={colors[role] || colors.user}>{role.replace('_', ' ')}</Badge>;
  };

  const getTierBadge = (tier: string): React.ReactElement => {
    const colors: Record<string, string> = {
      enterprise: 'bg-purple-500/10 text-purple-400',
      pro: 'bg-blue-500/10 text-blue-400',
      starter: 'bg-green-500/10 text-green-400',
      free: 'bg-slate-500/10 text-slate-400',
    };
    return <Badge className={colors[tier] || colors.free}>{tier}</Badge>;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="glass">
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">User Management</h1>
          <p className="text-slate-400 mt-1">View and manage all platform users</p>
        </div>
        <Button variant="outline" onClick={fetchUsers} disabled={loading}>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Refresh
        </Button>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="banned">Banned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Roles</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={v => { setTierFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Tiers</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Withdrawable</TableHead>
                <TableHead>Numbers</TableHead>
                <TableHead>Referrals</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length > 0 ? (
                users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-medium">
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.firstName} {user.lastName}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{getTierBadge(user.tier)}</TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(user.totalBalance)}</TableCell>
                    <TableCell className="font-mono text-green-400">{formatCurrency(user.withdrawableBalance)}</TableCell>
                    <TableCell>{user.activeNumbers}</TableCell>
                    <TableCell>{user.totalReferrals}</TableCell>
                    <TableCell className="text-slate-400">{formatDateTime(user.createdAt)}</TableCell>
                    <TableCell className="text-slate-400">{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {user.status === 'active' && (
                          <Button variant="ghost" size="icon" className="text-amber-400 hover:text-amber-300" onClick={() => handleAction(user, 'suspend')}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {user.status === 'suspended' && (
                          <Button variant="ghost" size="icon" className="text-green-400 hover:text-green-300" onClick={() => handleAction(user, 'unsuspend')}>
                            <User className="h-4 w-4" />
                          </Button>
                        )}
                        {user.status !== 'banned' && (
                          <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" onClick={() => handleAction(user, 'ban')}>
                            <Shield className="h-4 w-4" />
                          </Button>
                        )}
                        {user.status === 'banned' && (
                          <Button variant="ghost" size="icon" className="text-green-400 hover:text-green-300" onClick={() => handleAction(user, 'unban')}>
                            <Shield className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="text-cyan-400 hover:text-cyan-300" onClick={() => handleAction(user, 'reset_password')}>
                          <CreditCard className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-slate-400">No users found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-slate-400">Page {currentPage} of {totalPages}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionType.charAt(0).toUpperCase() + actionType.slice(1).replace('_', ' ')} User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">Are you sure you want to <strong>{actionType.replace('_', ' ')}</strong> <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong> ({selectedUser?.email})?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>Cancel</Button>
            <Button variant={['ban', 'suspend'].includes(actionType) ? 'destructive' : 'default'} onClick={confirmAction} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
